/**
 * orderHistoryService.ts
 * ---------------------------------------------------------------------------
 * PackageOrderScreen "Tarih Filtresi" (son 7 gün) için sadece okuma amaçlı
 * paralel servis layer'ı. Canlı akışa (SignalR, polling, fetch*Orders) hiç
 * dokunulmaz; bu fonksiyonlar yalnızca "Tarih Filtresi" aktifken çağrılır ve
 * sonuçları PackageOrderScreen'in `historic*` state grubuna yazılır.
 *
 * Backend tarafında tüm endpoint'ler zaten `startDate`/`endDate`/`branchId`
 * parametrelerini destekliyor; bu dosya frontend tarafında onları tek tip
 * imza altında toplar.
 *
 * NOT: MasaSiparisi parsing'i `useSignalR.fetchMasaSiparisleri` ile aynı
 * mantığı kullanır (qr/ecommerce/kiosk ayırımı, HS/RS hariç tutma). Tek fark
 * `isBusinessDayDate` kontrolünün KALDIRILMIŞ olması — kullanıcı zaten
 * geçmiş bir gün seçti, "iş günü" mantığı historic moda uymaz.
 */

import { migrosApi, yemeksepetiApi, mainApi } from '../../../../config/apiClient';
import type {
  GetirOrder,
  MigrosOrder,
  YemeksepetiOrder,
} from '../hooks/useSignalR';
import type { MasaSiparisi, MasaSiparisDetay } from '../types';

// ============================================================================
// GENEL YARDIMCILAR
// ============================================================================

/**
 * Date -> "YYYY-MM-DDTHH:mm:ss" (Z YOK!) local-format string.
 *
 * KRİTİK: Backend repository'lerin çoğu (Getir/Migros/YS/Trendyol) sorgu
 * yaparken `startDate.Value.Date` kullanıp tarihten saati siler ve UTC günü
 * alır. Frontend `.toISOString()` ile UTC olarak gönderirse Türkiye saati
 * 11 May 00:00 → UTC 10 May 21:00 olur → `.Date` = 10 May → off-by-one.
 *
 * Bunun yerine canlı fetcher'lardaki (`useSignalR.ts`) ile tutarlı şekilde
 * Z'siz local-format string gönderiyoruz. Bu durumda ASP.NET model binder
 * `DateTime.Kind = Unspecified` parse eder ve `.Date` doğru Türkiye gününü
 * verir (server local timezone Türkiye olduğu sürece).
 */
export function formatLocalDateTimeForApi(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}:${ss}`;
}

function buildDateRangeParams(startDate: Date, endDate: Date, branchId?: string | null): URLSearchParams {
  const params = new URLSearchParams();
  params.append('startDate', formatLocalDateTimeForApi(startDate));
  params.append('endDate', formatLocalDateTimeForApi(endDate));
  params.append('pageSize', '5000');
  if (branchId) params.append('branchId', branchId);
  return params;
}

// ============================================================================
// GETIR
// ============================================================================

export async function getGetirOrdersByDateRange(
  _startDate: Date,
  _endDate: Date,
  _branchId?: string | null,
): Promise<GetirOrder[]> {
  return [];
}

// ============================================================================
// MIGROS
// ============================================================================

export async function getMigrosOrdersByDateRange(
  startDate: Date,
  endDate: Date,
  branchId?: string | null,
): Promise<MigrosOrder[]> {
  try {
    if (!process.env.REACT_APP_MIGROSAPI_URL) {
      console.warn('[OrderHistory][Migros] REACT_APP_MIGROSAPI_URL tanımlı değil');
      return [];
    }
    const params = buildDateRangeParams(startDate, endDate, branchId);
    const url = `/api/migros-orders/list?${params.toString()}`;
    const { data: result } = await migrosApi.get(url);
    const data = result.data ?? result.Data;
    const success = result.success ?? result.Success;
    if (success && Array.isArray(data)) {
      console.log(`[OrderHistory][Migros] ${data.length} sipariş yüklendi`);
      return data as MigrosOrder[];
    }
    return [];
  } catch (error) {
    console.error('[OrderHistory][Migros] Yükleme hatası:', error);
    return [];
  }
}

// ============================================================================
// YEMEKSEPETI
// ============================================================================

export async function getYemeksepetiOrdersByDateRange(
  startDate: Date,
  endDate: Date,
  branchId?: string | null,
): Promise<YemeksepetiOrder[]> {
  try {
    if (!process.env.REACT_APP_YEMEKSEPETIAPI_URL) {
      console.warn('[OrderHistory][YS] REACT_APP_YEMEKSEPETIAPI_URL tanımlı değil');
      return [];
    }
    const params = buildDateRangeParams(startDate, endDate, branchId);
    const url = `/api/ys-orders/list?${params.toString()}`;
    const { data: result } = await yemeksepetiApi.get(url);
    const data = result.Data ?? result.data;
    const success = result.Success ?? result.success;
    if (success && Array.isArray(data)) {
      console.log(`[OrderHistory][YS] ${data.length} sipariş yüklendi`);
      return data as YemeksepetiOrder[];
    }
    return [];
  } catch (error) {
    console.error('[OrderHistory][YS] Yükleme hatası:', error);
    return [];
  }
}

// ============================================================================
// MASA SIPARISI (QR / E-Commerce / Kiosk)
// ============================================================================

export interface MasaSiparisleriByDateRangeResult {
  qrOrders: MasaSiparisi[];
  ecommerceOrders: MasaSiparisi[];
  kioskOrders: MasaSiparisi[];
}

/**
 * MasaSiparisi tablosundaki QR / E-ticaret / Kiosk siparişlerini tarih
 * aralığında çeker ve kaynaklarına göre ayırır.
 *
 * Parsing mantığı `useSignalR.fetchMasaSiparisleri`'nin birebir aynısıdır;
 * tek farkı `isBusinessDayDate` kontrolü uygulanmaz (geçmiş gün filtresi
 * için iş günü mantığı anlamsız).
 *
 * SUBE FILTRESI: Backend `branchId` param honoring etse de, canli akistaki
 * (useSignalR.fetchMasaSiparisleri) gibi frontend savunma katmani da
 * uygulanir. `isQrOrderForThisBranch` predicate'i geçilirse her kaynak
 * listesi bu predicate ile süzülür (BranchId atanmamış ama QRMenu.BranchId
 * eşleşen siparişler için kritik).
 */
export async function getMasaSiparisleriByDateRange(
  startDate: Date,
  endDate: Date,
  branchId?: string | null,
  isQrOrderForThisBranch?: (order: MasaSiparisi) => boolean,
): Promise<MasaSiparisleriByDateRangeResult> {
  try {
    const params: Record<string, any> = {
      // Z'siz local format — backend `.Date` mantığı için kritik (bkz.
      // formatLocalDateTimeForApi dokumantasyonu).
      startDate: formatLocalDateTimeForApi(startDate),
      endDate: formatLocalDateTimeForApi(endDate),
      pageSize: 5000,
    };
    if (branchId) params.branchId = branchId;

    const { data: result } = await mainApi.get('/api/MasaSiparisi/orders', { params });
    if (!result.success || !result.data) {
      return { qrOrders: [], ecommerceOrders: [], kioskOrders: [] };
    }

    const allOrders: MasaSiparisi[] = (result.data as any[]).map((order: any) => {
      let siparisDetaylari: MasaSiparisDetay[] = [];
      try {
        if (typeof order.siparisDetaylari === 'string') {
          const parsed = JSON.parse(order.siparisDetaylari || '[]');
          siparisDetaylari = parsed.siparisDetaylari || parsed || [];
        } else if (order.siparisDetaylari) {
          siparisDetaylari = order.siparisDetaylari.siparisDetaylari || order.siparisDetaylari || [];
        }
      } catch (e) {
        console.error('[OrderHistory][MasaSiparisi] Sipariş detayları parse hatası:', e);
      }

      return {
        id: order.id,
        siparisId: order.siparisId,
        masaNumarasi: order.masaNumarasi || order.masaId,
        masaAdi: order.masaAdi,
        kullaniciAdi: order.kullaniciAdi,
        siparisDurumu: order.siparisDurumu,
        siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
        siparisGuncellenmeTarihi: order.siparisGuncellenmeTarihi,
        toplamTutar: order.toplamTutar,
        qrMenuId: order.qrMenuId,
        branchId: order.branchId,
        siparisDetaylari,
        siparisNotu: order.siparisNotu,
        siparisKaynagi: order.siparisKaynagi,
        musteriAdi: order.musteriAdi || order.kullaniciAdi,
        musteriTelefon: order.musteriTelefon,
        musteriAdres: order.musteriAdres,
      } as MasaSiparisi;
    });

    // HS/RS siparişleri ve iptaller hariç (canlı akıştaki filtre ile aynı,
    // sadece isBusinessDayDate kaldırıldı çünkü zaten geçmiş gün seçili)
    const filteredOrders = allOrders.filter(order => {
      const durum = order.siparisDurumu?.toLowerCase() || '';
      if (durum === 'iptal' || durum === 'cancelled') return false;
      if (order.siparisId?.includes('-HS-')) return false;
      if (order.siparisId?.startsWith('RS-')) return false;
      const kaynak = (order.siparisKaynagi || '').toLowerCase();
      if (kaynak === 'restaurantquicksale') return false;
      return true;
    });

    const qrOrders: MasaSiparisi[] = [];
    const ecommerceOrders: MasaSiparisi[] = [];
    const kioskOrders: MasaSiparisi[] = [];

    filteredOrders.forEach(order => {
      let kaynak = (order.siparisKaynagi || '').toLowerCase();
      if (!kaynak && order.siparisId) {
        if (order.siparisId.startsWith('D-') || order.siparisId.startsWith('P-')) {
          kaynak = 'kiosk';
        } else {
          const parts = order.siparisId.split('-');
          if (parts.length >= 2) {
            const sourceType = parts[parts.length - 2].toLowerCase();
            if (sourceType === 'qr') kaynak = 'qrmenu';
            else if (sourceType === 'kiosk') kaynak = 'kiosk';
            else if (sourceType === 'ecom' || sourceType === 'ecommerce') kaynak = 'ecommerce';
          }
        }
      }

      if (kaynak.includes('ecommerce') || kaynak.includes('e-commerce') || kaynak.includes('ecom')) {
        ecommerceOrders.push(order);
      } else if (kaynak.includes('kiosk')) {
        kioskOrders.push(order);
      } else if (kaynak.includes('qr') || kaynak.includes('qrmenu')) {
        qrOrders.push(order);
      }
    });

    // SUBE SAVUNMA KATMANI: canli akistaki (useSignalR.fetchMasaSiparisleri)
    // gibi frontend tarafindan da sube filtresi uygulanir. Backend branchId
    // honoring etse de, eslestirme orada bazi sipariss icin BranchId NULL +
    // QRMenu.BranchId join'i uzerinden yapilir; bu predicate o senaryolari
    // dogru hallediyor.
    const finalQr = isQrOrderForThisBranch ? qrOrders.filter(isQrOrderForThisBranch) : qrOrders;
    const finalEcom = isQrOrderForThisBranch ? ecommerceOrders.filter(isQrOrderForThisBranch) : ecommerceOrders;
    const finalKiosk = isQrOrderForThisBranch ? kioskOrders.filter(isQrOrderForThisBranch) : kioskOrders;

    console.log('[OrderHistory][MasaSiparisi] Tarih aralığı yüklendi:', {
      qrRaw: qrOrders.length,
      ecomRaw: ecommerceOrders.length,
      kioskRaw: kioskOrders.length,
      qrFinal: finalQr.length,
      ecomFinal: finalEcom.length,
      kioskFinal: finalKiosk.length,
      branchFilter: branchId || 'YOK',
      hasFrontendPredicate: !!isQrOrderForThisBranch,
    });

    return { qrOrders: finalQr, ecommerceOrders: finalEcom, kioskOrders: finalKiosk };
  } catch (error) {
    console.error('[OrderHistory][MasaSiparisi] Yükleme hatası:', error);
    return { qrOrders: [], ecommerceOrders: [], kioskOrders: [] };
  }
}
